import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { listAlerts, listDevices, listRooms } from '../lib/adapters';
import { signalLabel } from '../lib/devices';
import { useI18n } from '../contexts/I18nContext';
import { useOrg } from '../contexts/OrgContext';
import { useRelativeTime } from '../lib/time';
import { useAlertText, alertSubject } from '../lib/alertText';
import { StatusChip, SeverityDot } from '../components/StatusChip';
import LastUpdated from '../components/LastUpdated';
import {
  UsersThree, WifiHigh, BellRinging, BatteryWarning, DoorOpen,
  PersonSimpleWalk, Pulse, CaretRight, ArrowUpRight,
} from '@phosphor-icons/react';
import { eventIcon } from '../lib/eventVisual';
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';

export default function Dashboard() {
  const { t, lang } = useI18n();
  const rel = useRelativeTime();
  const alertText = useAlertText();
  const [kpis, setKpis] = useState(null);
  const [falls, setFalls] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [devices, setDevices] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [events, setEvents] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAll = () => {
      Promise.all([
        api.get('/dashboard/kpis').then((r) => r.data).catch(() => null),
        api.get('/dashboard/falls-7d').then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []),
        listAlerts({ limit: 25 }).then((x) => x.items).catch(() => []),
        listDevices().then((x) => x.items).catch(() => []),
        listRooms().then((x) => x.items || []).catch(() => []),
        api.get('/events', { params: { limit: 50 } }).then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []),
      ]).then(([k, f, a, d, r, e]) => {
        if (cancelled) return;
        setKpis(k && typeof k === 'object' ? k : null);
        setFalls(f);
        setAlerts(a);
        setDevices(d);
        setRooms(r);
        setEvents(e.filter((ev) => ev.type !== 'heartbeat'));
        setLastRefresh(Date.now());
      });
    };
    fetchAll();
    const id = setInterval(() => { if (!document.hidden) fetchAll(); }, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ---- derived metrics for widgets ----
  const totalAlerts = alerts.length;
  const criticalAlerts = alerts.filter((a) => a.severity === 'critical' && a.status !== 'resolved').length;
  const unresolvedAlerts = alerts.filter((a) => a.status === 'new').length;
  const ackedAlerts = alerts.filter((a) => a.status === 'acknowledged').length;
  const criticalAlertsList = alerts.filter((a) => a.severity === 'critical' && a.status !== 'resolved').slice(0, 4);

  const onlineDevices = devices.filter((d) => d.connectivity_status === 'online').length;
  const offlineDevices = devices.filter((d) => d.connectivity_status === 'offline').length;
  const lowBattery = devices.filter((d) => d.battery != null && d.battery <= 20).length;
  const otaPending = devices.filter((d) => d.ota_status === 'pending' || d.ota_status === 'deploying').length;

  const wristbands = devices.filter((d) => d.type === 'wristband');
  const wristbandLowBattery = wristbands.filter((d) => (d.battery ?? 100) <= 20).length;
  const wristbandOffline = wristbands.filter((d) => d.connectivity_status === 'offline').length;

  const doorsOpen = rooms.filter((r) => r.door_state === 'open').length;
  const windowsOpen = rooms.filter((r) => r.window_state === 'open').length;
  const activeRoomAlerts = rooms.filter((r) => (r.offline_devices || 0) > 0).length;


  const chartData = falls.map((f) => ({
    date: new Date(f.date).toLocaleDateString(lang === 'lv' ? 'lv-LV' : 'en-GB', { weekday: 'short' }),
    falls: f.falls,
    sos: f.sos,
  }));

  return (
    <div className="space-y-8">
      <header className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-3">
            {t('brand')}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl text-ink leading-none" data-testid="dashboard-title">
            {t('dashboard_title')}
          </h1>
          <p className="text-ink-soft mt-3 max-w-xl leading-relaxed">{t('dashboard_sub')}</p>
        </div>
        <span
          data-testid="dashboard-autorefresh"
          className="inline-flex flex-col items-end gap-1 text-xs uppercase tracking-[0.18em] font-semibold text-sage-700 bg-sage-100 px-3 py-1.5 rounded-2xl"
        >
          <span className="inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-sage-600 animate-pulse" />
            {t('auto_refresh')}
          </span>
          <LastUpdated at={lastRefresh} testid="dashboard-last-updated" />
        </span>
      </header>

      {/* Row 1 — 4 KPI squares */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* 1. Active alerts */}
        <Widget
          title={t('widget_active_alerts')}
          subtitle={t('widget_active_alerts_sub')}
          icon={BellRinging}
          accent="terracotta"
          to="/alerts"
          testid="widget-active-alerts"
        >
          <MetricRow value={criticalAlerts} label={t('metric_critical')} tone="critical" />
          <MetricRow value={unresolvedAlerts} label={t('metric_unresolved')} tone="warning" />
          <MetricRow value={ackedAlerts} label={t('metric_acknowledged_count')} tone="ok" />
        </Widget>

        {/* 2. Patients status */}
        <Widget
          title={t('widget_patients_status')}
          icon={UsersThree}
          accent="sage"
          to="/patients"
          testid="widget-patients"
        >
          <MetricRow value={kpis?.total_residents ?? '—'} label={t('metric_total')} />
          <MetricRow value={(kpis?.total_residents ?? 0) - (kpis?.residents_critical ?? 0)} label={t('metric_active')} tone="ok" />
          <MetricRow value={wristbandLowBattery} label={t('metric_battery_warn')} tone={wristbandLowBattery > 0 ? 'warning' : 'muted'} />
          <MetricRow value={wristbandOffline} label={`${t('metric_offline')} (${t('device_bracelet')})`} tone={wristbandOffline > 0 ? 'critical' : 'muted'} />
        </Widget>

        {/* 3. Devices health */}
        <Widget
          title={t('widget_devices_health')}
          icon={WifiHigh}
          accent="ink"
          to="/devices"
          testid="widget-devices"
        >
          <MetricRow value={onlineDevices} label={t('metric_online')} tone="ok" />
          <MetricRow value={offlineDevices} label={t('metric_offline')} tone={offlineDevices > 0 ? 'critical' : 'muted'} />
          <MetricRow value={otaPending} label={t('metric_ota_pending')} tone={otaPending > 0 ? 'warning' : 'muted'} />
          <MetricRow value={lowBattery} label={t('metric_battery_warn')} tone={lowBattery > 0 ? 'warning' : 'muted'} />
        </Widget>

        {/* 4. Room activity */}
        <Widget
          title={t('widget_room_activity')}
          icon={DoorOpen}
          accent="amber"
          to="/rooms"
          testid="widget-rooms"
        >
          <MetricRow value={rooms.length} label={t('rooms_title')} />
          <MetricRow value={doorsOpen} label={t('metric_doors_open')} tone={doorsOpen > 0 ? 'warning' : 'muted'} />
          <MetricRow value={windowsOpen} label={t('metric_windows_open')} tone={windowsOpen > 0 ? 'warning' : 'muted'} />
          <MetricRow value={activeRoomAlerts} label={t('open_alerts')} tone={activeRoomAlerts > 0 ? 'warning' : 'muted'} />
        </Widget>
      </div>

      {/* Row 2 — Recent critical alerts + Live events feed (max 4 each) */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-stone_line shadow-soft p-6 animate-fade-up" data-testid="recent-alerts">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl text-ink">{t('recent_critical_alerts_title')}</h2>
            <Link to="/alerts" className="text-xs text-sage hover:text-sage-600 inline-flex items-center gap-1">
              {t('view_all')} <ArrowUpRight size={14} />
            </Link>
          </div>
          {criticalAlertsList.length === 0 ? (
            <div className="text-sm text-ink-soft py-3">{t('no_alerts')}</div>
          ) : (
            <ul className="divide-y divide-stone_line" data-testid="recent-alerts-list">
              {criticalAlertsList.map((a) => (
                <li key={a.id} className="py-3 flex items-start gap-3">
                  <SeverityDot severity={a.severity} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-medium text-ink truncate">{alertSubject(a, t)}</div>
                      <div className="text-xs text-ink-muted whitespace-nowrap">{rel(a.created_at)}</div>
                    </div>
                    <div className="text-sm text-ink-soft mt-0.5 truncate">{alertText(a)}</div>
                  </div>
                  <StatusChip status={a.severity === 'critical' ? 'critical' : 'warning'} />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-stone_line shadow-soft p-6 animate-fade-up" data-testid="widget-events">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-xl text-ink">{t('widget_recent_events')}</h3>
              <p className="text-xs text-ink-muted mt-0.5">{t('timeline_24h')}</p>
            </div>
            <Link to="/events" data-testid="widget-events-view-all" className="text-xs text-sage hover:text-sage-600 inline-flex items-center gap-1">
              {t('view_all')} <ArrowUpRight size={14} />
            </Link>
          </div>
          {events.length === 0 ? (
            <div className="text-sm text-ink-soft py-3">{t('no_results')}</div>
          ) : (
            <ul className="divide-y divide-stone_line" data-testid="dashboard-events-list">
              {events.slice(0, 4).map((ev) => {
                const EvIcon = eventIcon(ev.type);
                return (
                <li key={ev.id} className="py-3 flex items-center gap-3">
                  <SeverityDot severity={ev.severity} />
                  <div className="w-7 h-7 rounded-lg bg-bg-alt grid place-items-center text-ink-soft shrink-0">
                    <EvIcon size={14} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-ink truncate">{t(`event_${ev.type}`)}</div>
                    <div className="text-xs text-ink-muted truncate">{ev.resident_name} · {ev.message}</div>
                  </div>
                  <div className="text-xs text-ink-muted whitespace-nowrap">{rel(ev.created_at)}</div>
                </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {/* Row 3 — Falls & SOS graph, full width */}
      <section className="grid grid-cols-1 gap-5" data-testid="falls-section">
        <div className="bg-white rounded-2xl border border-stone_line shadow-soft p-6 animate-fade-up">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl text-ink">{t('falls_chart_title')}</h2>
            <PersonSimpleWalk size={20} className="text-sage-500" weight="duotone" />
          </div>
          <div className="h-[260px]" data-testid="falls-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barGap={8}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DF" vertical={false} />
                <XAxis dataKey="date" stroke="#A0A8A2" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#A0A8A2" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: '#fff', border: '1px solid #EAE6DF', borderRadius: 12,
                    fontSize: 12, boxShadow: '0 4px 24px rgba(43,48,44,0.08)',
                  }}
                />
                <Bar dataKey="falls" fill="#D48C76" radius={[6, 6, 0, 0]} />
                <Bar dataKey="sos" fill="#E5B27A" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>
    </div>
  );
}

function Widget({ title, subtitle, icon: Icon, accent = 'sage', to, testid, children }) {
  const accents = {
    sage: 'text-sage-600 bg-sage-100',
    terracotta: 'text-terracotta-700 bg-terracotta-100',
    amber: 'text-[#b4843f] bg-amber_warn-100',
    ink: 'text-ink bg-bg-alt',
  };
  const inner = (
    <>
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-xl grid place-items-center ${accents[accent]}`}>
          <Icon size={20} weight="duotone" />
        </div>
        {to && <CaretRight size={16} className="text-ink-muted" />}
      </div>
      <div>
        <h3 className="font-heading text-lg text-ink leading-tight">{title}</h3>
        {subtitle && <p className="text-xs text-ink-muted mt-1">{subtitle}</p>}
      </div>
      <div className="mt-5 space-y-2.5">{children}</div>
    </>
  );
  const cls = 'block bg-white rounded-2xl border border-stone_line shadow-soft p-6 animate-fade-up hover:shadow-card hover:-translate-y-0.5 transition-all';
  return to ? (
    <Link to={to} data-testid={testid} className={cls}>{inner}</Link>
  ) : (
    <div data-testid={testid} className={cls}>{inner}</div>
  );
}

function MetricRow({ value, label, tone = 'default' }) {
  const tones = {
    critical: 'text-terracotta-700',
    warning: 'text-[#b4843f]',
    ok: 'text-sage-700',
    muted: 'text-ink-muted',
    default: 'text-ink',
  };
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-xs text-ink-soft">{label}</span>
      <span className={`font-heading text-2xl tabular-nums ${tones[tone] || tones.default}`}>
        {value ?? '—'}
      </span>
    </div>
  );
}
